import React, { useState, useEffect } from 'react'
import axios from 'axios'
import toast from 'react-hot-toast'
import { useAuthStore } from '../../stores/authStore'
import { usePermissions } from '../../hooks/usePermissions'
import PageHelp from '../../components/PageHelp'

interface AccountingPeriod {
  id: string
  name: string
  startDate: string
  endDate: string
  isClosed: boolean
  closedAt?: string
  closedBy?: string
}

const AccountingPeriods: React.FC = () => {
  const { user } = useAuthStore()
  const { hasPermission, loading: permissionsLoading } = usePermissions(user)
  const [periods, setPeriods] = useState<AccountingPeriod[]>([])
  const [currentPeriod, setCurrentPeriod] = useState<AccountingPeriod | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [editingPeriod, setEditingPeriod] = useState<AccountingPeriod | null>(null)
  const [formData, setFormData] = useState({
    name: '',
    startDate: '',
    endDate: '',
  })

  useEffect(() => {
    fetchPeriods()
    fetchCurrentPeriod()
  }, [])

  const fetchPeriods = async () => {
    try {
      setIsLoading(true)
      const response = await axios.get('/api/erp/accounting-periods')
      setPeriods(response.data.data || [])
    } catch (error: any) {
      console.error('Error fetching accounting periods:', error)
      toast.error('Failed to load accounting periods')
    } finally {
      setIsLoading(false)
    }
  }

  const fetchCurrentPeriod = async () => {
    try {
      const response = await axios.get('/api/erp/accounting-periods/current')
      if (response.data.data) {
        setCurrentPeriod(response.data.data)
      }
    } catch (error: any) {
      // Current period might not exist, that's okay
      console.log('No current period found')
    }
  }

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.name || !formData.startDate || !formData.endDate) {
      toast.error('Please fill in all required fields')
      return
    }

    try {
      await axios.post('/api/erp/accounting-periods', {
        name: formData.name,
        startDate: formData.startDate,
        endDate: formData.endDate,
      })
      toast.success('Accounting period created successfully')
      setShowCreateModal(false)
      setFormData({
        name: '',
        startDate: '',
        endDate: '',
      })
      fetchPeriods()
      fetchCurrentPeriod()
    } catch (error: any) {
      console.error('Error creating accounting period:', error)
      toast.error(error.response?.data?.error || 'Failed to create accounting period')
    }
  }

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!editingPeriod) return

    if (!formData.name || !formData.startDate || !formData.endDate) {
      toast.error('Please fill in all required fields')
      return
    }

    try {
      await axios.put(`/api/erp/accounting-periods/${editingPeriod.id}`, {
        name: formData.name,
        startDate: formData.startDate,
        endDate: formData.endDate,
      })
      toast.success('Accounting period updated successfully')
      setShowEditModal(false)
      setEditingPeriod(null)
      fetchPeriods()
      fetchCurrentPeriod()
    } catch (error: any) {
      console.error('Error updating accounting period:', error)
      toast.error(error.response?.data?.error || 'Failed to update accounting period')
    }
  }

  const handleClose = async (id: string) => {
    if (!confirm('Are you sure you want to close this accounting period? This action cannot be undone and will prevent new transactions from being posted to this period.')) {
      return
    }
    try {
      await axios.post(`/api/erp/accounting-periods/${id}/close`)
      toast.success('Accounting period closed successfully')
      fetchPeriods()
      fetchCurrentPeriod()
    } catch (error: any) {
      console.error('Error closing accounting period:', error)
      toast.error(error.response?.data?.error || 'Failed to close accounting period')
    }
  }

  if (permissionsLoading) {
    return (
      <div className="p-6 text-center text-gray-500">Loading permissions...</div>
    )
  }

  if (!hasPermission('erp_accounting_period', 'read')) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">You don't have permission to view accounting periods.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6">
      <div className="mb-6 flex justify-between items-center">
        <div>
          <div className="flex items-center">
            <h1 className="text-2xl font-bold text-gray-900">Accounting Periods</h1>
            <PageHelp pageIdentifier="accounting-periods" title="Accounting Periods" />
          </div>
          <p className="text-gray-600 mt-1">Manage fiscal years and accounting periods</p>
          {currentPeriod && (
            <p className="text-sm text-blue-600 mt-1">
              Current Period: <strong>{currentPeriod.name}</strong> ({new Date(currentPeriod.startDate).toLocaleDateString()} - {new Date(currentPeriod.endDate).toLocaleDateString()})
            </p>
          )}
        </div>
        {hasPermission('erp_accounting_period', 'write') && (
          <button
            onClick={() => setShowCreateModal(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center space-x-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            <span>Create Period</span>
          </button>
        )}
      </div>

      {isLoading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : periods.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <p className="text-gray-500">No accounting periods found. Create your first period to get started.</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Start Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">End Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {periods.map((period) => (
                <tr key={period.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2">
                    <div className="flex items-center space-x-2">
                      {currentPeriod && currentPeriod.id === period.id && (
                        <span className="text-blue-600 font-semibold" title="Current Period">●</span>
                      )}
                      <span className={currentPeriod && currentPeriod.id === period.id ? 'font-semibold' : ''}>{period.name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-2">{new Date(period.startDate).toLocaleDateString()}</td>
                  <td className="px-4 py-2">{new Date(period.endDate).toLocaleDateString()}</td>
                  <td className="px-4 py-2">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      period.isClosed ? 'bg-gray-100 text-gray-800' : 'bg-green-100 text-green-800'
                    }`}>
                      {period.isClosed ? 'Closed' : 'Open'}
                    </span>
                  </td>
                  <td className="px-4 py-2">
                    <div className="flex space-x-2">
                      {!period.isClosed && hasPermission('erp_accounting_period', 'write') && (
                        <button
                          onClick={() => {
                            setEditingPeriod(period)
                            setFormData({
                              name: period.name,
                              startDate: new Date(period.startDate).toISOString().split('T')[0],
                              endDate: new Date(period.endDate).toISOString().split('T')[0],
                            })
                            setShowEditModal(true)
                          }}
                          className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                        >
                          Edit
                        </button>
                      )}
                      {!period.isClosed && hasPermission('erp_accounting_period', 'close') && (
                        <button
                          onClick={() => handleClose(period.id)}
                          className="text-orange-600 hover:text-orange-800 text-sm font-medium"
                        >
                          Close
                        </button>
                      )}
                      {period.isClosed && period.closedAt && (
                        <span className="text-xs text-gray-500">
                          Closed {new Date(period.closedAt).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Period Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h2 className="text-xl font-bold mb-4">Create Accounting Period</h2>
            <form onSubmit={handleCreate}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., FY 2024, Q1 2024"
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start Date *</label>
                  <input
                    type="date"
                    required
                    value={formData.startDate}
                    onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">End Date *</label>
                  <input
                    type="date"
                    required
                    value={formData.endDate}
                    onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                  />
                </div>
              </div>
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  Create Period
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Period Modal */}
      {showEditModal && editingPeriod && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h2 className="text-xl font-bold mb-4">Edit Accounting Period</h2>
            <form onSubmit={handleUpdate}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., FY 2024, Q1 2024"
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start Date *</label>
                  <input
                    type="date"
                    required
                    value={formData.startDate}
                    onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">End Date *</label>
                  <input
                    type="date"
                    required
                    value={formData.endDate}
                    onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                  />
                </div>
              </div>
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false)
                    setEditingPeriod(null)
                  }}
                  className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  Update Period
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default AccountingPeriods
